import API from '@aws-amplify/api';
import Utils from './helper.util';

// impersonateUserHeaderToken
export const impersonateUserHeaderToken = async () => {
  const impersonateUser: string = sessionStorage.getItem('user') || '';
  const isActive = !Utils.isEmptyStr(impersonateUser);
  const token = isActive && JSON.parse(impersonateUser).token;
  return token;
};

const createGetHeaders = async (
  context: ServiceContext,
  customHeader?: any
): Promise<any> => {
  const headers = { 'Content-Type': 'application/json' };

  await context.authenticationHandler(headers);

  return {
    method: 'GET',
    mode: context.mode,
    cache: 'no-cache',
    headers: customHeader || headers,
    redirect: 'follow'
  };
};

export const getRequest = async (
  context: ServiceContext,
  url: string,
  params?: any,
  customHeader?: any
) => {
  const payload = await createGetHeaders(context, customHeader);
  const responseBody = await API.get(context.serviceURL, url, {
    ...payload,
    queryStringParameters: {
      ...params
      //  forceTermsAcceptance: true // TODO:: Need to remove force TernsAcceptance once set bussiness rule
    }
  })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error?.response?.data;
    });
  return responseBody;
};

const handleFetchErrors = (response: any) => {
  if (!response.ok) {
    throw Error(response.status);
  }
  return response;
};

export const getFetchRequest = async (context: ServiceContext, url: string) => {
  const payload: any = await createGetHeaders(context);
  const URL = `${context.serviceURL}${url}`;
  const responseBody = await fetch(URL, payload)
    .then(handleFetchErrors)
    .then((response) => response.json())
    .catch((error) => error);
  return responseBody;
};

const createPostPayload = async (context: ServiceContext): Promise<any> => {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };
  await context.authenticationHandler(headers);
  return {
    method: 'POST',
    cache: 'no-cache',
    mode: context.mode,
    headers: headers,
    redirect: 'follow'
  };
};

const createDeletePayload = async (context: ServiceContext): Promise<any> => {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };
  await context.authenticationHandler(headers);
  return {
    method: 'DELETE',
    cache: 'no-cache',
    mode: context.mode,
    headers: headers,
    redirect: 'follow'
  };
};
const createPutPayload = async (context: ServiceContext): Promise<any> => {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };
  await context.authenticationHandler(headers);
  return {
    method: 'PUT',
    cache: 'no-cache',
    mode: context.mode,
    headers: headers,
    redirect: 'follow'
  };
};

const createPatchPayload = async (
  context: ServiceContext,
  payload: any
): Promise<RequestInit> => {
  const headers = { 'Content-Type': 'application/json' };

  await context.authenticationHandler(headers);
  return {
    method: 'PATCH',
    mode: context.mode,
    cache: 'no-cache',
    headers: headers,
    redirect: 'follow',
    body: JSON.stringify(payload)
  };
};

export const postRequest = async (
  context: ServiceContext,
  url: string,
  content?: Object
) => {
  const payload = await createPostPayload(context);
  const responseBody = await API.post(context.serviceURL, url, {
    ...payload,
    body: content
  })
    .then((response: any) => {
      return response;
    })
    .catch((error: any) => {
      return error.response?.data || error.response || error;
    });
  return responseBody;
};

export const deleteRequest = async (
  context: ServiceContext,
  url: string,
  content?: Object
) => {
  const payload = await createDeletePayload(context);

  return await API.del(context.serviceURL, url, {
    ...payload,
    body: content
  });
};
export const putRequest = async (
  context: ServiceContext,
  url: string,
  content?: Object
) => {
  const payload = await createPutPayload(context);
  const responseBody = await API.put(context.serviceURL, url, {
    ...payload,
    body: content
  })
    .then((response: any) => response)
    .catch((error: any) => {
      return error.response?.data || error.response || error;
    });
  return responseBody;
};

export const patchRequest = async (
  context: ServiceContext,
  url: string,
  content: any
) => {
  const payload = await createPatchPayload(context, content);
  const responseBody = await API.patch(context.serviceURL, url, {
    ...payload,
    body: content
  })
    .then((response: any) => response)
    .catch((error: any) => {
      return (
        error.response?.data ||
        error.response ||
        (error.error ? error : { error: { message: 'Unknown error' } }) //To cater if there is no error object inside error response
      );
    });
  return responseBody;
};

export const getImageRequest = async (
  context: ServiceContext,
  url: string,
  params?: any
): Promise<string> => {
  const headers: any = {
    Accept: 'image/jpeg, image/png, image/*'
  };
  await context.authenticationHandler(headers);

  const baseUrl = `${process.env.REACT_APP_RSOTF_API_URL}${url}`;
  const urlParams = new URLSearchParams(params);
  const fullUrl = params ? `${baseUrl}?${urlParams.toString()}` : baseUrl;

  const response = await fetch(fullUrl, {
    mode: context.mode,
    headers: headers
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.startsWith('image/')) {
    throw new Error(`Expected image, got ${contentType}`);
  }

  const base64Content = await response.text();

  if (!base64Content) {
    throw new Error('Received empty response');
  }

  return base64Content;
};

export class ServiceContext {
  serviceURL: string | undefined = undefined;
  authenticationHandler: (headers: Object) => {};
  mode: 'same-origin' | 'navigate' | 'cors' | 'no-cors' | undefined = 'cors';
  constructor(
    url: string | undefined,
    authenticationHandler: (headers: Object) => {}
  ) {
    this.serviceURL = url;
    this.authenticationHandler = authenticationHandler;
  }
}
